import { useTranslation } from "react-i18next";import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  ScanLine,
  Check,
  X,
  AlertTriangle,
  Package,
  MapPin,
  Calendar,
  Hash,
  FileText,
  Truck,
  Thermometer,
  QrCode,
  Barcode,
  Info,
  Copy,
  Camera,
  Keyboard,
  ChevronDown,
  ChevronUp } from
'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { cn } from '@/shared/utils';
import {
  parseBarcode,
  detectBarcodeType,
  validateBarcode,
  BarcodeType,
  SAMPLE_BARCODES } from
'@/shared/lib/barcodeParser';
import { CameraScanner } from './CameraScanner';

// Barcode type icons and colors
const barcodeTypeConfig = {
  [BarcodeType.GS1_128]: { icon: Barcode, color: 'blue', label: 'GS1-128' },
  [BarcodeType.GS1_DATAMATRIX]: { icon: QrCode, color: 'violet', label: 'DataMatrix' },
  [BarcodeType.NFE_ACCESS_KEY]: { icon: FileText, color: 'emerald', label: 'NF-e' },
  [BarcodeType.NFE_QR]: { icon: QrCode, color: 'emerald', label: 'NF-e QR' },
  [BarcodeType.CTE_ACCESS_KEY]: { icon: Truck, color: 'cyan', label: 'CT-e' },
  [BarcodeType.EAN_13]: { icon: Barcode, color: 'gray', label: 'EAN-13' },
  [BarcodeType.EAN_8]: { icon: Barcode, color: 'gray', label: 'EAN-8' },
  [BarcodeType.UPC_A]: { icon: Barcode, color: 'gray', label: 'UPC-A' },
  [BarcodeType.INTERNAL]: { icon: Package, color: 'indigo', label: 'Internal' },
  [BarcodeType.UNKNOWN]: { icon: AlertTriangle, color: 'amber', label: 'Unknown' }
};

// Field display config
const fieldConfig = {
  GTIN: { icon: Package, label: 'GTIN', priority: 1 },
  BATCH_LOT: { icon: Hash, label: 'Lot/Batch', priority: 2 },
  EXPIRY_DATE: { icon: Calendar, label: 'Expiry Date', priority: 3 },
  SERIAL: { icon: Hash, label: 'Serial #', priority: 4 },
  SSCC: { icon: Package, label: 'SSCC', priority: 1 },
  NET_WEIGHT_KG: { icon: Package, label: 'Weight', priority: 5 },
  CNPJ: { icon: FileText, label: 'CNPJ', priority: 2 },
  nNF: { icon: Hash, label: 'Invoice #', priority: 3 },
  serie: { icon: Hash, label: 'Series', priority: 4 },
  type: { icon: FileText, label: 'Type', priority: 1 },
  location: { icon: MapPin, label: 'Location', priority: 1 },
  accessKey: { icon: FileText, label: 'Access Key', priority: 1 }
};

// Parsed field display component
const ParsedField = ({ name, field }) => {const { t } = useTranslation("common");
  const config = fieldConfig[name] || { icon: Info, label: name, priority: 99 };
  const Icon = config.icon;

  return (
    <div className="flex items-start gap-2 py-2 px-3 bg-gray-50 rounded-lg">
      <Icon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-gray-500">{config.label}</div>
        <div className="font-mono text-sm text-gray-900 break-all">
          {field.parsed || field.raw}
        </div>
        {field.unit &&
        <span className="text-xs text-gray-500 ml-1">{field.unit}</span>
        }
      </div>
    </div>);

};

// Scan result display component
const ScanResult = ({ result, onClear, showRaw = false }) => {const { t } = useTranslation("common");
  const [expanded, setExpanded] = useState(true);
  const typeConfig = barcodeTypeConfig[result.type] || barcodeTypeConfig[BarcodeType.UNKNOWN];
  const TypeIcon = typeConfig.icon;

  // Sort fields by priority
  const sortedFields = Object.entries(result.fields).sort((a, b) => {
    const priorityA = fieldConfig[a[0]]?.priority || 99;
    const priorityB = fieldConfig[b[0]]?.priority || 99;
    return priorityA - priorityB;
  });

  return (
    <div className={cn(
      "rounded-xl border-2 overflow-hidden transition-all",
      result.valid ?
      "border-emerald-300 bg-emerald-50/50" :
      "border-red-300 bg-red-50/50"
    )}>
      {/* Header */}
      <div
        className={cn(
          "flex items-center justify-between p-3 cursor-pointer",
          result.valid ? "bg-emerald-100/50" : "bg-red-100/50"
        )}
        onClick={() => setExpanded(!expanded)}>
        
        <div className="flex items-center gap-2">
          {result.valid ?
          <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
              <Check className="h-5 w-5 text-white" />
            </div> :

          <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
              <X className="h-5 w-5 text-white" />
            </div>
          }
          <div>
            <div className="font-semibold text-gray-900">
              {result.valid ? 'Scan Valid' : 'Scan Invalid'}
            </div>
            <Badge
              variant="outline"
              className={cn(
                "text-xs",
                `bg-${typeConfig.color}-100 text-${typeConfig.color}-700 border-${typeConfig.color}-200`
              )}>
              
              <TypeIcon className="h-3 w-3 mr-1" />
              {typeConfig.label}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {e.stopPropagation();onClear();}}>{t("clear", "Clear")}


          </Button>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </div>

      {/* Content */}
      {expanded &&
      <div className="p-3 space-y-3">
          {/* Parsed fields */}
          <div className="grid grid-cols-2 gap-2">
            {sortedFields.map(([name, field]) =>
          <ParsedField key={name} name={name} field={field} />
          )}
          </div>

          {/* Errors */}
          {result.errors.length > 0 &&
        <div className="bg-red-100 border border-red-200 rounded-lg p-3">
              <div className="text-sm font-medium text-red-800 mb-1">{t("errors", "Errors:")}</div>
              {result.errors.map((error, idx) =>
          <div key={idx} className="text-sm text-red-700">• {error}</div>
          )}
            </div>
        }

          {/* Raw data */}
          {showRaw &&
        <div className="bg-gray-100 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">{t("rawData", "Raw Data")}</span>
                <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={() => navigator.clipboard.writeText(result.raw)}>
              
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
              <div className="font-mono text-xs text-gray-700 break-all">
                {result.raw}
              </div>
            </div>
        }
        </div>
      }
    </div>);

};

// Main Scanner Component
export const BarcodeScanner = ({
  onScan,
  expectedFields = null,
  placeholder = "Scan barcode or enter manually...",
  autoFocus = true,
  showSamples = false,
  allowManualEntry = true,
  allowCameraScan = true,
  scanMode = 'any', // 'any', 'gs1', 'nfe', 'internal'
  className
}) => {const { t } = useTranslation("common");
  const [inputValue, setInputValue] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [inputMode, setInputMode] = useState(allowCameraScan ? 'camera' : 'manual');
  const inputRef = useRef(null);

  // Auto-focus input
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Process scan
  const processScan = useCallback((data) => {
    if (!data.trim()) return;

    setIsProcessing(true);

    // Small delay to show processing state
    setTimeout(() => {
      const parsed = parseBarcode(data.trim());
      setScanResult(parsed);

      // Validate if expected fields provided
      if (expectedFields && parsed.valid) {
        const validation = validateBarcode(parsed, expectedFields);
        setValidationResult(validation);

        // Call onScan with validation result
        onScan?.({
          ...parsed,
          validation,
          isValid: validation.valid
        });
      } else {
        setValidationResult(null);
        onScan?.(parsed);
      }

      setIsProcessing(false);
    }, 100);
  }, [expectedFields, onScan]);

  // Handle input change (for paste or scanner input)
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Auto-process if value looks complete (common barcode lengths)
    // This handles scanner input that doesn't trigger Enter
    if (value.length === 44 || // NF-e access key
    value.length === 13 || // EAN-13
    value.length === 14 || // GTIN
    value.includes('\n') || // Scanner added newline
    value.endsWith('\t')) {// Scanner added tab
      processScan(value.replace(/[\n\t]/g, ''));
      setInputValue('');
    }
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      processScan(inputValue);
      setInputValue('');
    }
  };

  // Handle sample click
  const handleSampleClick = (sample) => {
    processScan(sample);
    setInputValue('');
  };

  const handleCameraScan = (decodedText) => {
    processScan(decodedText);
    setInputValue('');
  };

  // Clear results
  const handleClear = () => {
    setScanResult(null);
    setValidationResult(null);
    setInputValue('');
    inputRef.current?.focus();
  };

  return (
    <div className={cn("space-y-4", className)} data-testid="barcode-scanner">
      {allowCameraScan &&
      <div className="flex items-center gap-2">
          <Button
          type="button"
          variant={inputMode === 'camera' ? 'default' : 'outline'}
          size="sm"
          className="gap-2"
          onClick={() => setInputMode('camera')}
          data-testid="camera-scan-mode">
          
            <Camera className="h-4 w-4" />{t("camera", "Camera")}
          </Button>
          {allowManualEntry &&
        <Button
          type="button"
          variant={inputMode === 'manual' ? 'default' : 'outline'}
          size="sm"
          className="gap-2"
          onClick={() => setInputMode('manual')}
          data-testid="manual-scan-mode">
          
            <Keyboard className="h-4 w-4" />{t("manual", "Manual")}
          </Button>
        }
        </div>
      }

      {allowCameraScan && inputMode === 'camera' &&
      <CameraScanner
        onScan={handleCameraScan}
        autoStart={false}
        scannerConfig={{
          fps: 10,
          qrbox: { width: 280, height: 180 },
          rememberLastUsedCamera: true
        }} />
      }

      {/* Scanner Input */}
      {allowManualEntry && inputMode === 'manual' &&
      <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2">
            {isProcessing ?
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /> :

          <ScanLine className="h-5 w-5 text-gray-400" />
          }
          </div>
          <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="pl-10 pr-4 h-12 text-lg font-mono"
        disabled={isProcessing}
        data-testid="barcode-input" />
          
        </div>
      }

      {/* Scan Mode Indicator */}
      {scanMode !== 'any' &&
      <div className="flex items-center gap-2 text-sm text-gray-500">
          <Info className="h-4 w-4" />
          <span>{t("expecting", "Expecting:")}
          {
          scanMode === 'gs1' ? 'GS1-128 or DataMatrix' :
          scanMode === 'nfe' ? 'NF-e / CT-e' :
          scanMode === 'internal' ? 'Internal barcode' :
          'Any barcode'
          }
          </span>
        </div>
      }

      {/* Expected Fields */}
      {expectedFields &&
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="text-sm font-medium text-blue-800 mb-2">{t("expectedValues", "Expected Values:")}</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(expectedFields).map(([key, value]) =>
          <Badge key={key} variant="outline" className="bg-white">
                {fieldConfig[key]?.label || key}: {value}
              </Badge>
          )}
          </div>
        </div>
      }

      {/* Scan Result */}
      {scanResult &&
      <ScanResult
        result={scanResult}
        onClear={handleClear}
        showRaw />

      }

      {/* Validation Result */}
      {validationResult &&
      <div className={cn(
        "rounded-lg border p-3",
        validationResult.valid ?
        "border-emerald-200 bg-emerald-50" :
        "border-red-200 bg-red-50"
      )}>
          <div className="flex items-center gap-2 mb-2">
            {validationResult.valid ?
          <>
                <Check className="h-5 w-5 text-emerald-600" />
                <span className="font-medium text-emerald-800">{t("validationPassed", "Validation Passed")}</span>
              </> :

          <>
                <X className="h-5 w-5 text-red-600" />
                <span className="font-medium text-red-800">{t("validationFailed", "Validation Failed")}</span>
              </>
          }
          </div>
          {validationResult.mismatches.length > 0 &&
        <div className="text-sm text-red-700">
              {validationResult.mismatches.map((m, idx) =>
          <div key={idx}>• {m}</div>
          )}
            </div>
        }
        </div>
      }

      {/* Sample Barcodes */}
      {showSamples &&
      <div className="border-t pt-4">
          <div className="text-sm font-medium text-gray-700 mb-2">{t("testSamples", "Test Samples:")}</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(SAMPLE_BARCODES).map(([name, value]) =>
          <Button
            key={name}
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => handleSampleClick(value)}>
            
                {name.replace(/_/g, ' ')}
              </Button>
          )}
          </div>
        </div>
      }
    </div>);

};

// Quick scan button component (for integration into other components)
export const QuickScanButton = ({ onScan, label = "Scan", className }) => {const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        className={cn("gap-2", className)}
        onClick={() => setIsOpen(true)}>
        
        <ScanLine className="h-4 w-4" />
        {label}
      </Button>

      {isOpen &&
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">{t("scanBarcode", "Scan Barcode")}</h3>
              <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <BarcodeScanner
            onScan={(result) => {
              onScan?.(result);
              if (result.valid) setIsOpen(false);
            }}
            showSamples />
          
          </div>
        </div>
      }
    </>);

};

export default BarcodeScanner;
